import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Toast, ToastVariant, ToastAction } from '../components/common/Toast';

interface ToastData {
  id: string;
  message: string;
  variant: ToastVariant;
  duration?: number;
  action?: ToastAction;
}

interface ToastOptions {
  variant?: ToastVariant;
  duration?: number;
  action?: ToastAction;
}

interface ToastContextValue {
  /** Show a toast with options, returns toast ID */
  showToast: (message: string, options?: ToastOptions) => string;
  showSuccess: (message: string, action?: ToastAction) => string;
  showError: (message: string) => string;
  /** Dismiss a specific toast by ID */
  dismissToast: (id: string) => void;
  /** Dismiss all toasts */
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_VISIBLE_TOASTS = 3;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const showToast = useCallback(
    (message: string, options: ToastOptions = {}): string => {
      const id = Math.random().toString(36).substring(2, 9);
      const { variant = 'info', duration = 5000, action } = options;

      setToasts((prev) => {
        const newToasts = [...prev, { id, message, variant, duration, action }];
        // Keep only the most recent MAX_VISIBLE_TOASTS
        if (newToasts.length > MAX_VISIBLE_TOASTS) {
          return newToasts.slice(-MAX_VISIBLE_TOASTS);
        }
        return newToasts;
      });

      return id;
    },
    []
  );

  const showSuccess = useCallback((message: string, action?: ToastAction): string => {
    return showToast(message, { variant: 'success', action });
  }, [showToast]);

  const showError = useCallback((message: string): string => {
    return showToast(message, { variant: 'error' });
  }, [showToast]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError, dismissToast, dismissAll }}>
      {children}
      {/* Render toasts with stacking - positioned at top to avoid covering FAB, water buttons, and offline banner */}
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          style={{
            position: 'fixed',
            top: `${16 + index * 68}px`,
            left: '16px',
            right: '16px',
            zIndex: 50 + index,
          }}
        >
          <Toast
            message={toast.message}
            variant={toast.variant}
            duration={toast.duration}
            action={toast.action}
            onDismiss={() => dismissToast(toast.id)}
          />
        </div>
      ))}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
